import React, { useEffect, useState } from "react";
import {
  FaCreditCard,
  FaTimes,
  FaCheckCircle,
  FaExclamationCircle,
  FaLock,
  FaSave,
  FaSpinner,
  FaSlidersH,
} from "react-icons/fa";
import api from "../services/api";
import "./PaymentSettingsTenant.css";

const PERCENTUAIS = [10, 25, 30, 50, 100];

export default function PaymentSettingsTenant() {
  const [aberto, setAberto] = useState(false);
  const [config, setConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const percentuaisPermitidos = config
    ? PERCENTUAIS.filter((valor) => config[`permitir_${valor}`])
    : [];

  const servicosElegiveis = services.filter(
    (service) => service.elegivel_pagamento
  );

  const ativo = Boolean(config?.pagamento_online_ativo);

  const load = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const { data } = await api.get(
        "/admin/pagamentos/configuracoes",
        { withCredentials: true }
      );

      const received = data.configuracao;
      const options = PERCENTUAIS.filter(
        (value) => received[`permitir_${value}`]
      );

      const percentualAtual = Number(received.percentual_pagamento);
      const percentualValido = options.includes(percentualAtual)
        ? percentualAtual
        : options[0];

      setConfig({
        ...received,
        percentual_pagamento: percentualValido ?? 50,
      });

      setServices(data.servicos || []);
      setSelected(
        (data.servicos_selecionados || []).map(Number)
      );
    } catch (e) {
      setError(
        e.response?.data?.error ||
          "Não foi possível carregar as configurações de pagamento."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (aberto) load();
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) {
        setAberto(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [aberto, saving]);

  const fechar = () => {
    if (!saving) setAberto(false);
  };

  const toggleService = (id) => {
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id]
    );
  };

  const atualizarConfig = (campo, valor) => {
    setConfig((current) => ({
      ...current,
      [campo]: valor,
    }));
    setSuccess("");
  };

  const save = async () => {
    if (!config || !config.pagamento_online_liberado) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const { data } = await api.put(
        "/admin/pagamentos/configuracoes",
        {
          pagamento_online_ativo: Boolean(
            config.pagamento_online_ativo
          ),
          percentual_pagamento: Number(
            config.percentual_pagamento
          ),
          modo_servicos: config.modo_servicos,
          servicos_selecionados: selected,
        },
        { withCredentials: true }
      );

      setConfig((current) => ({
        ...current,
        ...data.configuracao,
      }));

      setSuccess(data.message || "Configurações salvas com sucesso.");
    } catch (e) {
      setError(
        e.response?.data?.error ||
          "Não foi possível salvar as configurações."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="payment-settings-trigger">
        <button
          type="button"
          className="payment-open-button"
          onClick={() => setAberto(true)}
        >
          <span className="payment-trigger-icon">
            <FaCreditCard />
          </span>

          <span className="payment-trigger-text">
            <strong>Pagamento antecipado</strong>
            <small>
              {config
                ? ativo
                  ? "Ativado"
                  : "Configurar pagamentos"
                : "Gerencie as cobranças online"}
            </small>
          </span>

          <span
            className={`payment-status-dot ${
              ativo ? "is-active" : ""
            }`}
            aria-label={ativo ? "Ativado" : "Desativado"}
          />
        </button>
      </div>

      {aberto && (
        <div
          className="payment-modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) fechar();
          }}
        >
          <section
            className="payment-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="payment-modal-title"
          >
            <header className="payment-modal-header">
              <div className="payment-modal-heading">
                <div className="payment-modal-icon">
                  <FaSlidersH />
                </div>

                <div>
                  <h2 id="payment-modal-title">
                    Pagamento antecipado
                  </h2>
                  <p>
                    Configure como e quando seus clientes pagam.
                  </p>
                </div>
              </div>

              <button
                type="button"
                className="payment-close-button"
                onClick={fechar}
                disabled={saving}
                aria-label="Fechar configurações"
              >
                <FaTimes />
              </button>
            </header>

            <div className="payment-modal-body">
              {loading ? (
                <div className="payment-loading">
                  <FaSpinner className="payment-spin" />
                  <span>Carregando configurações...</span>
                </div>
              ) : !config ? (
                <div className="payment-alert payment-alert-error" role="alert">
                  <FaExclamationCircle />
                  <span>
                    {error || "Configurações indisponíveis."}
                  </span>
                  <button
                    type="button"
                    className="payment-retry-button"
                    onClick={load}
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <>
                  {!config.pagamento_online_liberado && (
                    <div className="payment-alert payment-alert-warning">
                      <FaLock />
                      <span>
                        Você ainda não tem acesso a esse recurso, entre
                        em contato com o Suporte Cronos.
                      </span>
                    </div>
                  )}

                  <section className="payment-config-section">
                    <div className="payment-section-title">
                      <div>
                        <h3>Recebimento online</h3>
                        <p>
                          Defina se os clientes precisarão pagar antes
                          do atendimento.
                        </p>
                      </div>
                    </div>

                    <label
                      className={`payment-toggle-card ${
                        ativo ? "is-enabled" : ""
                      }`}
                    >
                      <span className="payment-toggle-copy">
                        <strong>Ativar pagamento antecipado</strong>
                        <small>
                          {ativo
                            ? "O recurso está ativado."
                            : "O recurso está desativado."}
                        </small>
                      </span>

                      <input
                        type="checkbox"
                        checked={ativo}
                        disabled={
                          !config.pagamento_online_liberado || saving
                        }
                        onChange={(event) =>
                          atualizarConfig(
                            "pagamento_online_ativo",
                            event.target.checked
                          )
                        }
                      />

                      <span className="payment-switch" />
                    </label>
                  </section>

                  <section className="payment-config-section">
                    <div className="payment-section-title">
                      <div>
                        <h3>Percentual de entrada</h3>
                        <p>
                          Quanto do valor do serviço será cobrado
                          antecipadamente.
                        </p>
                      </div>
                    </div>

                    {percentuaisPermitidos.length > 0 ? (
                      <div className="payment-percentage-grid">
                        {percentuaisPermitidos.map((valor) => (
                          <label
                            key={valor}
                            className={`payment-percentage-option ${
                              Number(config.percentual_pagamento) === valor
                                ? "is-selected"
                                : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name="percentual-pagamento"
                              value={valor}
                              checked={
                                Number(config.percentual_pagamento) === valor
                              }
                              disabled={saving}
                              onChange={() =>
                                atualizarConfig(
                                  "percentual_pagamento",
                                  valor
                                )
                              }
                            />

                            <strong>{valor}%</strong>
                            <small>
                              {valor === 100
                                ? "Valor total"
                                : valor === 50
                                  ? "Metade"
                                  : "Entrada"}
                            </small>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="payment-inline-warning">
                        Nenhum percentual está habilitado para esta
                        conta. Verifique as permissões no painel Cronos.
                      </p>
                    )}
                  </section>

                  <section className="payment-config-section">
                    <div className="payment-section-title">
                      <div>
                        <h3>Serviços participantes</h3>
                        <p>
                          Escolha em quais serviços será exigido o
                          pagamento antecipado.
                        </p>
                      </div>
                    </div>

                    <div className="payment-service-modes">
                      <label
                        className={`payment-mode-option ${
                          config.modo_servicos === "todos"
                            ? "is-selected"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="modo-servicos"
                          checked={config.modo_servicos === "todos"}
                          disabled={saving}
                          onChange={() =>
                            atualizarConfig("modo_servicos", "todos")
                          }
                        />

                        <span className="payment-mode-radio" />
                        <span>
                          <strong>Todos os serviços</strong>
                          <small>
                            Aplicar aos serviços com preço definido.
                          </small>
                        </span>
                      </label>

                      <label
                        className={`payment-mode-option ${
                          config.modo_servicos === "selecionados"
                            ? "is-selected"
                            : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="modo-servicos"
                          checked={config.modo_servicos === "selecionados"}
                          disabled={saving}
                          onChange={() =>
                            atualizarConfig(
                              "modo_servicos",
                              "selecionados"
                            )
                          }
                        />

                        <span className="payment-mode-radio" />
                        <span>
                          <strong>Somente serviços selecionados</strong>
                          <small>
                            Escolher individualmente quais participarão.
                          </small>
                        </span>
                      </label>
                    </div>

                    {config.modo_servicos === "selecionados" && (
                      <div className="payment-service-list">
                        <div className="payment-service-list-header">
                          <strong>Serviços elegíveis</strong>
                          <span>
                            {selected.filter((id) =>
                              servicosElegiveis.some(
                                (service) => Number(service.id) === id
                              )
                            ).length}{" "}
                            selecionado(s)
                          </span>
                        </div>

                        {servicosElegiveis.length > 0 ? (
                          servicosElegiveis.map((service) => {
                            const id = Number(service.id);

                            return (
                              <label
                                key={service.id}
                                className="payment-service-item"
                              >
                                <input
                                  type="checkbox"
                                  checked={selected.includes(id)}
                                  disabled={saving}
                                  onChange={() => toggleService(id)}
                                />

                                <span className="payment-service-check" />

                                <span className="payment-service-name">
                                  {service.nome}
                                </span>

                                <strong className="payment-service-price">
                                  {Number(service.preco).toLocaleString(
                                    "pt-BR",
                                    {
                                      style: "currency",
                                      currency: "BRL",
                                    }
                                  )}
                                </strong>
                              </label>
                            );
                          })
                        ) : (
                          <p className="payment-empty-services">
                            Não há serviços com preço definido elegíveis
                            para pagamento antecipado.
                          </p>
                        )}

                        <p className="payment-service-note">
                          Serviços sem preço definido ou marcados como
                          “A Definir” não podem exigir pagamento online.
                        </p>
                      </div>
                    )}
                  </section>

                  <div className="payment-info-note">
                    <FaLock />
                    <p>
                      As alterações afetam somente novos agendamentos.
                      Pagamentos já iniciados mantêm os valores e
                      percentuais registrados.
                    </p>
                  </div>

                  {error && (
                    <div
                      className="payment-alert payment-alert-error"
                      role="alert"
                    >
                      <FaExclamationCircle />
                      <span>{error}</span>
                    </div>
                  )}

                  {success && (
                    <div
                      className="payment-alert payment-alert-success"
                      role="status"
                    >
                      <FaCheckCircle />
                      <span>{success}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {!loading && config && (
              <footer className="payment-modal-footer">
                <button
                  type="button"
                  className="payment-cancel-button"
                  onClick={fechar}
                  disabled={saving}
                >
                  Fechar
                </button>

                <button
                  type="button"
                  className="payment-save-button"
                  onClick={save}
                  disabled={
                    saving ||
                    !config.pagamento_online_liberado ||
                    percentuaisPermitidos.length === 0
                  }
                >
                  {saving ? (
                    <>
                      <FaSpinner className="payment-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Salvar configurações
                    </>
                  )}
                </button>
              </footer>
            )}
          </section>
        </div>
      )}
    </>
  );
}